import JobViewModel from '../../JobViewModel'
import { observer } from 'mobx-react'
import React from 'react'
import {
  Section,
  RequestedSupportingItem,
  DocumentSupportingItemInput,
  PersonalInfoSupportingItemInput,
  SpousalInfoSupportingItemInput,
  DependentsInfoSupportingItemInput,
  BusinessInfoSupportingItemInput,
  PersonalRefundMethodInfoSupportingItemInput,
  BusinessRefundMethodInfoSupportingItemInput,
  NotesSupportingItemInput,
} from '../../../../../Checklist'
import { CapturedInformationTab } from '../../DetailsView/DetailsViewModel'
import { DocumentsSectionTab } from '../../JobDocumentsViewModel'
import styles from './SupportingItemChecklist.sass'
import cx from 'classnames'
import Icon from '../../../../../../components/Icon'

interface SupportingItemChecklistProps {
  vm: JobViewModel
}

export const SupportingItemChecklist = observer(
  ({ vm }: SupportingItemChecklistProps) => {
    return (
      <div>
        {vm.documentsVM.supportingItems.map((section, key) => {
          return <SupportingItemSection key={key} section={section} vm={vm} />
        })}
      </div>
    )
  },
)

interface SupportingItemSectionProps {
  section: Section
  vm: JobViewModel
}

const SupportingItemSection = observer(
  ({ section, vm }: SupportingItemSectionProps) => {
    return (
      <div
        className={styles.section}
        data-test={`checklist-supporting-items-section-${section.name}`}
      >
        <div className={styles.sectionHeader}>
          {section.heading}
          <div className={styles.sectionHeaderSubtext}>
            ({`${section.completedCount}/${section.totalCount}`})
          </div>
        </div>
        <div className={styles.sectionList}>
          {section.requestedSupportingItems.map((item, index) => {
            const target = getClickTarget(item)
            const isClickable = target !== null
            return (
              <div
                key={index}
                data-test={`checklist-supporting-items-item-${item.name}`}
                className={cx(
                  styles.supportingItem,
                  isClickable && styles.clickable,
                )}
                onClick={
                  isClickable
                    ? () => {
                        if (target.type === 'capturedInfo') {
                          vm.detailsVM.capturedInformationTabController.change(
                            target.tab,
                          )
                          document
                            .getElementById('captured-information-card')
                            ?.scrollIntoView({
                              behavior: 'smooth',
                              block: 'nearest',
                            })
                        } else {
                          vm.documentsVM.documentsTabController.change(
                            DocumentsSectionTab.Documents,
                          )
                        }
                      }
                    : undefined
                }
              >
                <div
                  className={cx(styles.icon, getItemStyle(item))}
                  data-test={`checklist-supporting-items-item-${item.name}-completion-status`}
                >
                  {item.completed ? (
                    <Icon name="tick" />
                  ) : item.adHoc ? (
                    <Icon name="alert-avatar" />
                  ) : (
                    <Icon name="circle" />
                  )}
                </div>
                <div
                  data-test={`checklist-supporting-items-item-${item.name}-name`}
                  className={styles.name}
                >
                  {item.name}
                  {item.required && (
                    <span
                      data-test={`checklist-supporting-items-item-${item.name}-required`}
                      className={styles.required}
                    >
                      *
                    </span>
                  )}
                </div>
                <div
                  className={cx(styles.providedInfo, getItemStyle(item))}
                  data-test={`checklist-supporting-items-item-${item.name}-provided`}
                >
                  {formatProvidedText(item)}
                </div>
                {isClickable && (
                  <div className={styles.linkIcon}>
                    <Icon name="arrow" size={16} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  },
)

type ClickTarget =
  | { type: 'capturedInfo'; tab: CapturedInformationTab }
  | { type: 'documents' }

/**
 * Returns the navigation target for a completed checklist item,
 * or null if the item is not navigable.
 */
function getClickTarget(item: RequestedSupportingItem): ClickTarget | null {
  if (!item.completed || !item.input) return null

  if (
    item.input instanceof PersonalInfoSupportingItemInput ||
    item.input instanceof SpousalInfoSupportingItemInput ||
    item.input instanceof DependentsInfoSupportingItemInput
  ) {
    return { type: 'capturedInfo', tab: CapturedInformationTab.PersonalInfo }
  }
  if (item.input instanceof BusinessInfoSupportingItemInput) {
    return { type: 'capturedInfo', tab: CapturedInformationTab.BusinessInfo }
  }
  if (
    item.input instanceof PersonalRefundMethodInfoSupportingItemInput ||
    item.input instanceof BusinessRefundMethodInfoSupportingItemInput
  ) {
    return { type: 'capturedInfo', tab: CapturedInformationTab.RefundInfo }
  }
  if (item.input instanceof NotesSupportingItemInput) {
    return { type: 'capturedInfo', tab: CapturedInformationTab.Notes }
  }
  if (item.input instanceof DocumentSupportingItemInput) {
    return { type: 'documents' }
  }
  return null
}

/**
 * Formats the "Provided" text based on the input type.
 *
 * @param item
 */
function formatProvidedText(item: RequestedSupportingItem): string {
  if (item.adHoc && !item.completed) {
    return 'Requested'
  }

  switch (true) {
    case item.input instanceof DocumentSupportingItemInput: {
      return `${item.input!.documents.length} provided`
    }
    default: {
      if (item.completed) {
        return 'Provided'
      }
      return 'Not provided'
    }
  }
}

function getItemStyle(item: RequestedSupportingItem) {
  return item.completed
    ? styles.provided
    : item.adHoc
      ? styles.requested
      : styles.notProvided
}
